import { Alert, Stack, Text, Box } from '@mantine/core';
import { Image as ImageIcon } from 'react-feather';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import type { SessionAgendaVisual } from '../../../../../services/api/sessionAgenda';
import { SlidePageCanvas } from '../../../../../components/common/slides/SlidePageCanvas';

/**
 * MED-975 (E1.3d / AC-PS3, AC-PS4) — Section Visuals picker.
 *
 * Renders a thumbnail grid of every slide in the uploaded deck PDF. Each
 * thumbnail is toggleable as multi-select; a slide that's already
 * associated with a different section can still be selected here (same
 * slide may appear in more than one section per AC-PS3).
 *
 * No-deck handling per AC-PS4: when `hasSlideDeck` is false, shows an
 * inline affordance with text guiding the CM to upload one — does NOT
 * block saving the section.
 *
 * Implementation note: the parent (SessionContentEditorPage) loads the
 * slide deck PDF once and passes the `PDFDocumentProxy` down so multiple
 * SectionVisualsPicker instances don't each load the PDF independently.
 */

const THUMBNAIL_WIDTH = 140;

export interface SectionVisualsPickerProps {
  hasSlideDeck: boolean;
  /** Loaded PDF doc — null while loading or when no deck. */
  slideDeckDoc: PDFDocumentProxy | null;
  visuals: SessionAgendaVisual[];
  onChange: (next: SessionAgendaVisual[]) => void;
}

export function SectionVisualsPicker({
  hasSlideDeck,
  slideDeckDoc,
  visuals,
  onChange,
}: SectionVisualsPickerProps) {
  // No-deck affordance — never blocks saving (AC-PS4).
  if (!hasSlideDeck) {
    return (
      <Alert color="gray" variant="light" icon={<ImageIcon size={16} />}>
        <Text size="sm">
          No slide deck uploaded — upload one above to associate slides with this section.
        </Text>
      </Alert>
    );
  }

  if (!slideDeckDoc) {
    // Deck exists but PDF still loading — render nothing rather than a
    // spinner per-section (the page header already shows loading state).
    return null;
  }

  const selectedPages = new Set(visuals.map(v => v.slide_page));

  const togglePage = (page: number) => {
    if (selectedPages.has(page)) {
      onChange(
        visuals
          .filter(v => v.slide_page !== page)
          // Renumber sort_order so the M-of-N display stays gap-free.
          .map((v, i) => ({ ...v, sort_order: i + 1 })),
      );
    } else {
      onChange([
        ...visuals,
        { slide_page: page, sort_order: visuals.length + 1 },
      ]);
    }
  };

  // Selection position (1-of-N) for displaying order on selected thumbnails.
  const positionByPage = new Map<number, number>();
  visuals
    .slice()
    .sort((a, b) => a.sort_order - b.sort_order)
    .forEach((v, i) => positionByPage.set(v.slide_page, i + 1));

  return (
    <Stack gap="xs" data-testid="agenda-section-visuals-picker">
      <Text size="xs" c="dimmed">
        Click a slide to associate it with this section. The same slide can be in more than one section.
      </Text>
      <Box
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(auto-fill, minmax(${THUMBNAIL_WIDTH}px, 1fr))`,
          gap: 8,
        }}
      >
        {Array.from({ length: slideDeckDoc.numPages }, (_, i) => i + 1).map(pageNum => (
          <SlideThumbnail
            key={pageNum}
            doc={slideDeckDoc}
            pageNum={pageNum}
            selected={selectedPages.has(pageNum)}
            selectionPosition={positionByPage.get(pageNum) ?? null}
            onClick={() => togglePage(pageNum)}
          />
        ))}
      </Box>
    </Stack>
  );
}

interface SlideThumbnailProps {
  doc: PDFDocumentProxy;
  pageNum: number;
  selected: boolean;
  selectionPosition: number | null;
  onClick: () => void;
}

/**
 * Mirrors the SlideThumbnail in SlideDeckPreview but adds a selected state
 * (Medley primary green border + position chip). The page render itself is the
 * shared `SlidePageCanvas` (high-DPI canvas, cancellation guard, transition-in).
 */
function SlideThumbnail({
  doc,
  pageNum,
  selected,
  selectionPosition,
  onClick,
}: SlideThumbnailProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      aria-label={`${selected ? 'Deselect' : 'Select'} slide ${pageNum}`}
      style={{
        background: 'white',
        border: selected
          ? '2px solid var(--mantine-color-medleyGreen-6)'
          : '1px solid var(--mantine-color-gray-3)',
        borderRadius: 6,
        padding: 4,
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden',
        aspectRatio: '16 / 10',
      }}
    >
      <SlidePageCanvas doc={doc} pageNum={pageNum} width={THUMBNAIL_WIDTH} style={{ width: '100%' }} />
      {/* page number chip (bottom-right) — matches SlideDeckPreview */}
      <div
        style={{
          position: 'absolute',
          bottom: 4,
          right: 4,
          background: 'rgba(0,0,0,0.65)',
          color: 'white',
          fontSize: 10,
          padding: '2px 6px',
          borderRadius: 4,
          fontWeight: 600,
        }}
      >
        {pageNum}
      </div>
      {/* selection-order chip (top-left) — only when selected */}
      {selected && selectionPosition !== null && (
        <div
          style={{
            position: 'absolute',
            top: 4,
            left: 4,
            background: 'var(--mantine-color-medleyGreen-6)',
            color: 'white',
            fontSize: 10,
            padding: '2px 6px',
            borderRadius: 999,
            fontWeight: 700,
            minWidth: 18,
            textAlign: 'center',
          }}
        >
          {selectionPosition}
        </div>
      )}
    </button>
  );
}
